import { useCallback, useEffect, useState } from "react";

import * as api from "./lib/api";
import { SessionExpired } from "./lib/api";
import { completeHostedUiSignIn, currentIdentity, signOut } from "./lib/auth";
import type { Identity } from "./lib/auth";
import { loadConfig } from "./lib/config";
import type { AppConfig } from "./lib/config";
import { SORT_LABELS } from "./lib/types";
import type { ProgramSummary, ResetResult, SortKey, WhoAmI } from "./lib/types";
import SignIn from "./components/SignIn";
import StudentList from "./components/StudentList";
import DetailView from "./components/DetailView";
import SearchPanel from "./components/SearchPanel";

const REFRESH_INTERVAL_MS = 8000;

/** Wait this long after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * What the reset actually did, in a sentence a presenter can read off the screen.
 *
 * Reporting nothing-to-do explicitly matters: pressing the button twice should say
 * "already clean" rather than look like it silently failed the second time.
 */
function describeReset(result: ResetResult): string {
  const parts: string[] = [];
  if (result.notes_removed > 0) {
    parts.push(
      `${result.notes_removed} advising note${result.notes_removed === 1 ? "" : "s"} removed`,
    );
  }
  if (result.students_holds_restored > 0) {
    parts.push(
      `holds restored for ${result.students_holds_restored} student${
        result.students_holds_restored === 1 ? "" : "s"
      }`,
    );
  }
  return parts.length > 0
    ? `Demo data reset — ${parts.join(", ")}.`
    : "Already matching the seeded baseline; nothing to undo.";
}

export default function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [whoami, setWhoami] = useState<WhoAmI | null>(null);
  const [programs, setPrograms] = useState<ProgramSummary[]>([]);
  const [currentTerm, setCurrentTerm] = useState<string>("");

  const [program, setProgram] = useState<string>("");
  const [sort, setSort] = useState<SortKey>("name");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Two pieces of state, not one: `searchInput` tracks the keystrokes so the field
  // stays responsive, `query` is what the panel actually fetches on.
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  // The staff-only "reset demo data" control: idle → confirm → running. Confirmation is
  // inline rather than a window.confirm() so it cannot be suppressed by the browser
  // mid-demo, and it is required because the reset deletes notes.
  const [resetState, setResetState] = useState<"idle" | "confirm" | "running">("idle");
  const [resetNotice, setResetNotice] = useState<string | null>(null);

  // Boot: load config, then restore any existing session.
  useEffect(() => {
    loadConfig()
      .then(async (cfg) => {
        setConfig(cfg);
        // If we came back from the Entra hosted-UI redirect (?code=...), complete
        // that exchange; otherwise restore any existing native/federated session.
        const federated = await completeHostedUiSignIn(cfg);
        setIdentity(federated ?? (await currentIdentity(cfg)));
      })
      .catch((err) => setBootError(err instanceof Error ? err.message : "Failed to start."))
      .finally(() => setBooting(false));
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (config) signOut(config);
    setIdentity(null);
    setWhoami(null);
    setNotice("Your session expired. Please sign in again.");
  }, [config]);

  // Load role + catalogue once signed in.
  useEffect(() => {
    if (!config || !identity) return;
    let cancelled = false;
    Promise.all([api.whoami(config, identity), api.programs(config, identity)])
      .then(([who, progs]) => {
        if (cancelled) return;
        setWhoami(who);
        setPrograms(progs.programs);
        setCurrentTerm(progs.current_term);
        setProgram((p) => p || progs.programs[0]?.program_code || "");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpired) handleSessionExpired();
        else setBootError(err instanceof Error ? err.message : "Failed to load.");
      });
    return () => {
      cancelled = true;
    };
  }, [config, identity, handleSessionExpired]);

  // Debounce: one request when typing settles, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Poll so writes the assistant makes (a note, a hold) show up here on their own.
  useEffect(() => {
    if (!autoRefresh || !identity) return;
    const timer = setInterval(() => setRefreshKey((k) => k + 1), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, identity]);

  function handleSignOut() {
    if (config) signOut(config);
    setIdentity(null);
    setWhoami(null);
    setSelectedId(null);
    setSearchInput("");
    setQuery("");
    setNotice(null);
    setResetState("idle");
    setResetNotice(null);
  }

  async function handleReset() {
    if (!config || !identity) return;
    setResetState("running");
    setResetNotice(null);
    try {
      const result = await api.resetDemoData(config, identity);
      setResetNotice(describeReset(result));
      // Force the list and the open record to re-fetch: a note that just vanished from
      // the table should vanish from the screen now, not on the next poll.
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof SessionExpired) handleSessionExpired();
      else setResetNotice(err instanceof Error ? err.message : "The reset failed.");
    } finally {
      setResetState("idle");
    }
  }

  if (booting) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (bootError) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 px-4">
        <p className="max-w-md rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {bootError}
        </p>
      </div>
    );
  }
  if (!config) return null;
  if (!identity) {
    return (
      <div>
        {notice && (
          <div className="bg-amber-50 py-2 text-center text-sm text-amber-700">{notice}</div>
        )}
        <SignIn config={config} onSignedIn={setIdentity} />
      </div>
    );
  }

  const isStaff = whoami?.is_staff ?? identity.isStaff;

  return (
    <div className="min-h-dvh bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <h1 className="text-base font-semibold text-slate-900">Student Management System</h1>
            <p className="text-xs text-slate-400">
              {currentTerm ? `Current term ${currentTerm} · ` : ""}
              mock Student Management System
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Staff only — and the API refuses it for a student regardless. */}
            {isStaff &&
              (resetState === "confirm" ? (
                <span className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800">
                  Undo the notes and holds this demo added?
                  <button onClick={handleReset} className="font-semibold underline">
                    Reset
                  </button>
                  <button
                    onClick={() => setResetState("idle")}
                    className="text-amber-700/80 underline"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setResetState("confirm")}
                  disabled={resetState === "running"}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:text-slate-400"
                  title="Restore the seeded baseline: removes advising notes and holds added since the last seed"
                >
                  {resetState === "running" ? "Resetting…" : "Reset demo data"}
                </button>
              ))}
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
              title="Auto-refresh reflects changes the assistant makes"
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  autoRefresh ? "animate-pulse bg-emerald-500" : "bg-slate-300"
                }`}
              />
              {autoRefresh ? "Live" : "Paused"}
            </button>
            <span
              className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                isStaff
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`}
            >
              {isStaff ? "Staff" : "Student"}
            </span>
            <span className="hidden text-sm text-slate-500 sm:inline">
              {identity.name ?? identity.email}
            </span>
            <button
              onClick={handleSignOut}
              className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {resetNotice && (
        <div className="border-b border-slate-200 bg-slate-100">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2 text-sm text-slate-700">
            <span>{resetNotice}</span>
            <button
              onClick={() => setResetNotice(null)}
              aria-label="Dismiss"
              className="text-slate-400 hover:text-slate-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-4 py-6">
        {isStaff ? (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <select
                value={program}
                onChange={(e) => {
                  setProgram(e.target.value);
                  setSelectedId(null);
                }}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
              >
                {programs.map((p) => (
                  <option key={p.program_code} value={p.program_code}>
                    {p.title} ({p.program_code})
                  </option>
                ))}
              </select>
              {/* Sorting is disabled while a search is showing: search results are
                  ordered by how well they match what was typed, which is the more
                  useful order there and not one of these. */}
              <label className="flex items-center gap-2 text-sm text-slate-500">
                Sort
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  disabled={Boolean(query)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400"
                >
                  {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
                    <option key={key} value={key}>
                      {SORT_LABELS[key]}
                    </option>
                  ))}
                </select>
              </label>

              {/* Search spans every program, so it replaces the list rather than
                  filtering it — the program selector above still describes the list
                  you return to when the box is cleared. */}
              <div className="relative ml-auto">
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search name or student id…"
                  aria-label="Search students by name or student id"
                  className="w-64 rounded-md border border-slate-300 py-1.5 pl-3 pr-8 text-sm placeholder:text-slate-400 focus:border-slate-500 focus:outline-none"
                />
                {searchInput && (
                  <button
                    onClick={() => setSearchInput("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
              {/* The list runs to thousands of rows, so bound it to the viewport and
                  let it scroll inside itself — otherwise picking a student off the
                  bottom of the list means scrolling the record beside it off the top.
                  Only from `lg`: stacked on a narrow screen, a nested scroll area is
                  worse than the page scroll it replaces. */}
              <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white lg:sticky lg:top-24 lg:max-h-[calc(100dvh-7.5rem)]">
                {query ? (
                  <SearchPanel
                    config={config}
                    identity={identity}
                    query={query}
                    refreshKey={refreshKey}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onSessionExpired={handleSessionExpired}
                  />
                ) : (
                  program && (
                    <StudentList
                      config={config}
                      identity={identity}
                      program={program}
                      sort={sort}
                      refreshKey={refreshKey}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      onSessionExpired={handleSessionExpired}
                    />
                  )
                )}
              </div>
              <div>
                {selectedId ? (
                  <DetailView
                    config={config}
                    identity={identity}
                    studentId={selectedId}
                    refreshKey={refreshKey}
                    onSessionExpired={handleSessionExpired}
                  />
                ) : (
                  <div className="grid h-64 place-items-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-400">
                    Select a student to see their full record.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl">
            <DetailView
              config={config}
              identity={identity}
              studentId={null}
              refreshKey={refreshKey}
              onSessionExpired={handleSessionExpired}
            />
          </div>
        )}
      </main>
    </div>
  );
}
